import { useCallback, useEffect, useRef } from 'react';
import { Box, Grid2 } from '@mui/material';
import { FlashDesignWithRelations } from '../../types/flashbooks';
import FlashDesignCard from './FlashDesignCard';
import { SortableGrid } from '../dnd/SortableGrid';

interface Props {
  designs: FlashDesignWithRelations[];
  onSelect: (design: FlashDesignWithRelations) => void;
  onEdit?: (design: FlashDesignWithRelations) => void;
  onCopyLink?: (design: FlashDesignWithRelations) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFetchNextPage?: () => void;
  isReorderMode?: boolean;
  onReorderChange?: (designs: FlashDesignWithRelations[]) => void;
}

export default function FlashbookDesignsGrid({
  designs,
  onSelect,
  onEdit,
  onCopyLink,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  isReorderMode = false,
  onReorderChange,
}: Props) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry?.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage &&
        onFetchNextPage
      ) {
        onFetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, onFetchNextPage],
  );

  useEffect(() => {
    if (isReorderMode) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '200px',
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [handleIntersect, isReorderMode]);

  if (isReorderMode) {
    return (
      <SortableGrid
        items={designs}
        onChange={next => onReorderChange?.(next)}
        renderItem={design => (
          <Box sx={{ position: 'relative' }}>
            <FlashDesignCard design={design} onClick={() => undefined} />
            <SortableGrid.DragHandle />
          </Box>
        )}
      />
    );
  }

  return (
    <Box>
      <Grid2 container spacing={2.5} columns={12}>
        {designs.map(design => (
          <Grid2 key={design.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <FlashDesignCard
              design={design}
              onClick={() => onSelect(design)}
              onEdit={onEdit ? () => onEdit(design) : undefined}
              onCopyLink={onCopyLink ? () => onCopyLink(design) : undefined}
            />
          </Grid2>
        ))}
      </Grid2>
      <Box ref={sentinelRef} sx={{ height: 1 }} />
    </Box>
  );
}
